import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiService } from '../services/apiService';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import VirtualConversationList from '../components/VirtualConversationList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

const ConversationsPage = () => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Connecté');

  // Use infinite scroll for calls with memory limit
  const {
    data: callsData,
    loading: loadingCalls,
    hasMore: hasMoreCalls,
    observerTarget: callsObserverTarget
  } = useInfiniteScroll(
    async (page, limit) => {
      const response = await apiService.calls.getAll({ page, limit });
      return response;
    },
    {
      initialPage: 1,
      pageSize: 20,
      enabled: true,
      maxItemsInMemory: 1000 // Keep only last 1000 items in memory
    }
  );

  // Map calls to conversation format
  const conversations = (callsData || []).map(call => ({
    id: call._id || call.id,
    phone: call.phone || call.receiver || call.to,
    campaign_name: call.campaign?.name || 'Manual Call',
    duration: call.duration || 0,
    created_at: call.createdAt || call.created_at,
    transcript: call.transcript || []
  }));

  const handleConversationClick = (conv) => {
    setSelectedConversation(conv);
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Conversations Temps Réel</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Live Conversation Panel */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Conversation en Direct</h2>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-600">{connectionStatus}</span>
              </div>
            </div>
            
            {/* Conversation Display - Same format as ManualCallsPage */}
            <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto mb-4">
              {selectedConversation && selectedConversation.transcript && selectedConversation.transcript.length > 0 ? (
                <div className="space-y-3">
                  {selectedConversation.transcript.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'agent' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          message.role === 'agent'
                            ? 'bg-blue-100 text-blue-900'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <p className="text-xs font-medium mb-1">
                          {message.role === 'agent' ? 'IA' : 'Prospect'}
                        </p>
                        <p className="text-sm">{message.text}</p>
                        {message.timestamp && (
                          <p className="text-xs opacity-70 mt-1">
                            {new Date(message.timestamp).toLocaleTimeString('fr-FR')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedConversation ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Aucune transcription disponible pour cet appel
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Sélectionnez une conversation pour commencer
                </div>
              )}
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsRecording(!isRecording)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isRecording
                      ? 'bg-red-500 text-white'
                      : 'bg-green-500 text-white'
                  }`}
                >
                  {isRecording ? '⏹️ Arrêter' : '🎤 Enregistrer'}
                </button>
              </div>
              <div className="text-sm text-gray-500">
                Pipeline: STT → LLM → TTS
              </div>
            </div>
          </div>

          {/* Conversations List */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4">Historique</h3>
            <VirtualConversationList
              conversations={conversations}
              selectedConversation={selectedConversation}
              onConversationClick={handleConversationClick}
              height={400}
              itemHeight={100}
              hasMore={hasMoreCalls}
              loading={loadingCalls}
              observerTarget={callsObserverTarget}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationsPage;